import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateStatus, translateUrgency, translatePartnerType } from '../i18n/translations';
import { hasExpress } from '../utils/express';
import { getImageUrl } from '../utils/imageUrl';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowRight, Package, MapPin, Archive as ArchiveIcon, ChevronDown, ChevronRight, ImageIcon, Globe, Bell, ExternalLink, Truck, CheckCircle, LayoutDashboard, FileText, AlertTriangle, Factory } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { StatusFlowBar } from '../components/StatusFlowBar';
import { TrackingDialog } from '../components/TrackingDialog';
import { DeliveredBadge } from '../components/DeliveredBadge';
import { TransportDashboard } from './TransportDashboard';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

const getRowColor = (order, partnerType) => {
  const now = new Date();
  if (partnerType === 'couture') {
    const bureauDate = order.bureau_delivery_date ? new Date(order.bureau_delivery_date) : null;
    if (!bureauDate) return '';
    const diffDays = Math.floor((now - bureauDate) / (1000 * 60 * 60 * 24));
    if (diffDays > 3) return 'bg-red-50 border-l-4 border-l-red-500';
    if (diffDays >= 1) return 'bg-amber-50 border-l-4 border-l-amber-500';
    return 'bg-emerald-50 border-l-4 border-l-emerald-500';
  }
  if (partnerType === 'imprimeur') {
    const orderDate = order.order_date ? new Date(order.order_date) : null;
    if (!orderDate) return '';
    const diffDays = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
    if (diffDays >= 5) return 'bg-red-50 border-l-4 border-l-red-500';
    if (diffDays >= 3) return 'bg-amber-50 border-l-4 border-l-amber-500';
    return 'bg-emerald-50 border-l-4 border-l-emerald-500';
  }
  return '';
};

export const PartnerDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weightInputs, setWeightInputs] = useState({});
  const [addressOrder, setAddressOrder] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingLink, setTrackingLink] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingDetailOpen, setTrackingDetailOpen] = useState(false);
  const [trackingDetailNumber, setTrackingDetailNumber] = useState('');
  const [notifBanner, setNotifBanner] = useState(null);
  const [alertNotifs, setAlertNotifs] = useState([]);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [quantityOrder, setQuantityOrder] = useState(null);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [packageOrderId, setPackageOrderId] = useState(null);
  const [packageWeight, setPackageWeight] = useState('');
  const [packagePrice, setPackagePrice] = useState('');
  const [selectedForColis, setSelectedForColis] = useState({});
  const [colisDialogOpen, setColisDialogOpen] = useState(false);
  const [colisWeight, setColisWeight] = useState('');
  const [colisPrice, setColisPrice] = useState('');
  const [transportPackages, setTransportPackages] = useState([]);
  const [transportInvoices, setTransportInvoices] = useState([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState({});
  // Checkin colis creation
  const [checkinSelectedOrders, setCheckinSelectedOrders] = useState({});
  const [checkinColisName, setCheckinColisName] = useState('');
  const [checkinColisDialogOpen, setCheckinColisDialogOpen] = useState(false);
  const [checkinPackages, setCheckinPackages] = useState([]);
  const [activeTransportTab, setActiveTransportTab] = useState('orders');
  const prevOrderCount = useRef(null);
  const prevColorKeys = useRef(null);
  // Production stats
  const [prodStats, setProdStats] = useState(null);
  const [lateDialogOpen, setLateDialogOpen] = useState(false);
  const prevOrderStatuses = useRef(null);

  // My Info popup
  const [myInfo, setMyInfo] = useState(null);
  const [myInfoOpen, setMyInfoOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    fetchMyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const fetchMyInfo = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/my-info`, { withCredentials: true });
      setMyInfo(res.data);
    } catch (e) { console.error('Error fetching partner info:', e); }
  };

  const handleChangePassword = async () => {
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error(t('confirm_password'));
      return;
    }
    if (pwdForm.new_password.length < 4) {
      toast.error('Mot de passe trop court');
      return;
    }
    setPwdLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/partner/change-password`, {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password
      }, { withCredentials: true });
      toast.success(t('password_changed'));
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowChangePassword(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('password_error'));
    } finally {
      setPwdLoading(false);
    }
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      toast.success(t('notifications_enabled'));
      new Notification('Panther', { body: t('notifications_enabled'), icon: LOGO_URL });
    } else {
      toast.error(t('notifications_denied'));
    }
  };

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.value = 0.3;
        osc2.start();
        osc2.stop(ctx.currentTime + 0.3);
      }, 200);
    } catch (e) { console.error('Audio playback not available:', e); }
  }, []);

  const fetchOrders = useCallback(async (isPolling = false) => {
    try {
      const [ordersRes, archivesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/orders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/archives`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      // Fetch production stats (for imprimeur/couture)
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/stats`, { withCredentials: true })
        .then(r => setProdStats(r.data)).catch(() => {});
      const newOrders = ordersRes.data;

      if (isPolling) {
        let newOrderDetected = false;
        let statusChangeDetected = false;
        let notifMsg = '';

        // Detect new orders
        if (prevOrderCount.current !== null && newOrders.length > prevOrderCount.current) {
          const diff = newOrders.length - prevOrderCount.current;
          notifMsg = diff === 1 ? t('new_order_notification') : `${diff} ${t('new_orders_notification')}`;
          newOrderDetected = true;
        }

        // Detect status changes
        if (prevOrderStatuses.current) {
          for (const o of newOrders) {
            const prevStatus = prevOrderStatuses.current[o.id];
            if (prevStatus && prevStatus !== o.status) {
              notifMsg = t('status_update_notification');
              statusChangeDetected = true;
              break;
            }
          }
        }

        if (newOrderDetected) {
          setNotifBanner(notifMsg);
          playNotifSound();
          setTimeout(() => setNotifBanner(null), 8000);
        } else if (statusChangeDetected) {
          setNotifBanner(notifMsg);
          playNotifSound();
          setTimeout(() => setNotifBanner(null), 8000);
        }

        // Detect orange/red color alerts
        const pt = user?.partner_type;
        const getColor = (order) => {
          const now = new Date();
          if (pt === 'couture') {
            const bd = order.bureau_delivery_date ? new Date(order.bureau_delivery_date) : null;
            if (!bd) return 'green';
            const diff = Math.floor((now - bd) / 86400000);
            return diff > 3 ? 'red' : diff >= 1 ? 'orange' : 'green';
          }
          if (pt === 'imprimeur') {
            const od = order.order_date ? new Date(order.order_date) : null;
            if (!od) return 'green';
            const diff = Math.floor((now - od) / 86400000);
            return diff >= 5 ? 'red' : diff >= 3 ? 'orange' : 'green';
          }
          return 'green';
        };
        const currentKeys = new Set(newOrders.filter(o => ['orange', 'red'].includes(getColor(o))).map(o => `${o.id}-${getColor(o)}`));
        const prev = prevColorKeys.current || new Set();
        const newColorAlerts = [...currentKeys].filter(k => !prev.has(k));
        if (newColorAlerts.length > 0) {
          const hasRed = newColorAlerts.some(k => k.endsWith('-red'));
          const msg = hasRed ? t('alert_red_notification') : t('alert_orange_notification');
          setAlertNotifs(prev => [...prev, { id: Date.now(), msg }]);
          playNotifSound();
          setTimeout(() => setAlertNotifs([]), 8000);
        }
        prevColorKeys.current = currentKeys;
      }

      prevOrderCount.current = newOrders.length;
      // Track statuses for change detection
      const statusMap = {};
      newOrders.forEach(o => { statusMap[o.id] = o.status; });
      prevOrderStatuses.current = statusMap;

      setOrders(newOrders);
      setArchives(archivesRes.data);
      const inputs = {};
      newOrders.forEach(o => { inputs[o.id] = o.package_weight_kg || ''; });
      setWeightInputs(inputs);

      // Fetch transport packages and invoices for transport partner
      if (user?.partner_type === 'transport') {
        try {
          const [pkgRes, invRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport/packages`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport/invoices`, { withCredentials: true }).catch(() => ({ data: [] })),
          ]);
          setTransportPackages(pkgRes.data || []);
          setTransportInvoices(invRes.data || []);
        } catch (e) { console.error('Transport fetch error:', e); }
      }

      // Fetch checkin packages
      if (user?.partner_type === 'checkin') {
        try {
          const pkgRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/packages`, { withCredentials: true }).catch(() => ({ data: [] }));
          setCheckinPackages(pkgRes.data || []);
        } catch (e) { console.error('Checkin packages error:', e); }
      }
    } catch (error) {
      console.error('Error fetching orders:', error?.response?.status, error?.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  }, [t, playNotifSound, user?.partner_type]);

  useEffect(() => {
    fetchOrders(false);
    const interval = setInterval(() => fetchOrders(true), 300000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const toggleMonth = (label) => {
    setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`${t('status')}: ${translateStatus(newStatus, t)}`);
      fetchOrders(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openTrackingDialog = (order) => {
    setTrackingOrder(order);
    setTrackingLink(order.tracking_link || '');
    setTrackingNumber(order.tracking_number || '');
    setTrackingDialogOpen(true);
  };

  const handleSaveTracking = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${trackingOrder.id}/tracking`, {
        tracking_link: trackingLink,
        tracking_number: trackingNumber
      }, { withCredentials: true });
      toast.success('Tracking mis à jour');
      setTrackingDialogOpen(false);
      fetchOrders(false);
    } catch { toast.error('Erreur'); }
  };

  const handleWeightUpdate = async (orderId) => {
    const weight = parseFloat(weightInputs[orderId]);
    if (isNaN(weight) || weight < 0) { toast.error('Invalid'); return; }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner/orders/${orderId}/weight`,
        { package_weight_kg: weight },
        { withCredentials: true }
      );
      toast.success(`${t('weight_label')}: ${weight} kg`);
      fetchOrders(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openPackageDialog = (orderId) => {
    setPackageOrderId(orderId);
    setPackageWeight('');
    setPackagePrice('');
    setPackageDialogOpen(true);
  };

  const handleValidatePackage = async () => {
    if (!packageWeight || !packagePrice) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/transport-orders/${packageOrderId}/validate-package`, {
        package_weight: parseFloat(packageWeight),
        package_price_vnd: parseFloat(packagePrice)
      }, { withCredentials: true });
      toast.success(t('package_validated'));
      setPackageDialogOpen(false);
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const selectedColisIds = Object.keys(selectedForColis).filter(k => selectedForColis[k]);

  const openColisDialog = () => {
    if (selectedColisIds.length < 1) { toast.error(t('select_at_least_2')); return; }
    setColisWeight('');
    setColisPrice('');
    setColisDialogOpen(true);
  };

  const handleValidateColis = async () => {
    if (!colisWeight || !colisPrice) { toast.error(t('weight_price_required')); return; }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/transport/packages`, {
        order_ids: selectedColisIds,
        weight: parseFloat(colisWeight),
        price_vnd: parseFloat(colisPrice)
      }, { withCredentials: true });
      toast.success(`${t('package_validated')} (${selectedColisIds.length} commandes)`);
      setColisDialogOpen(false);
      setSelectedForColis({});
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateInvoice = async () => {
    const ids = Object.keys(selectedPackageIds).filter(k => selectedPackageIds[k]);
    if (!ids.length) { toast.error('Sélectionnez au moins un colis'); return; }
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/transport/invoices`, {
        package_ids: ids
      }, { withCredentials: true });
      toast.success(`Facture N°${res.data.invoice_number} créée`);
      setSelectedPackageIds({});
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleCreateCheckinColis = async () => {
    const ids = Object.keys(checkinSelectedOrders).filter(k => checkinSelectedOrders[k]);
    if (!ids.length || !checkinColisName.trim()) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/checkin/packages`, {
        order_ids: ids, name: checkinColisName.trim()
      }, { withCredentials: true });
      toast.success(`Colis "${checkinColisName.trim()}" créé`);
      setCheckinColisDialogOpen(false);
      setCheckinSelectedOrders({});
      setCheckinColisName('');
      fetchOrders(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownloadInvoicePdf = async (invoiceId, invoiceNum) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/transport/invoices/${invoiceId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_Transport_${invoiceNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error('Erreur téléchargement PDF'); }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, { language: lang }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const allowedStatuses = user?.allowed_statuses || [];
  const normalizeStatus = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Explicit full status flow per partner type (must match backend PARTNER_DEFAULT_STATUSES)
  const FULL_FLOW = {
    imprimeur: ['commandée', 'impression', 'couture'],
    couture: ['couture', 'livraison bureau'],
    transport: ['en cours de livraison client', 'livrée'],
  };
  const statusFlow = FULL_FLOW[user?.partner_type] || allowedStatuses;

  const getNextStatus = (orderStatus) => {
    const orderNorm = normalizeStatus(orderStatus);
    const idx = statusFlow.findIndex(s => normalizeStatus(s) === orderNorm);
    if (idx >= 0 && idx < statusFlow.length - 1) return statusFlow[idx + 1];
    return null;
  };
  const hasStatusAction = statusFlow.length > 1;
  const partnerType = user?.partner_type;
  const isTransport = partnerType === 'transport';
  const isCouture = partnerType === 'couture';
  const isImprimeur = partnerType === 'imprimeur';
  const isCreateurFichiers = partnerType === 'createur_fichiers';
  const isCheckin = partnerType === 'checkin';

  // Transport partners use a dedicated dashboard with sidebar
  if (isTransport) return <TransportDashboard />;

  const showWeight = false;
  const showLivraisonPrevue = false;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 relative">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="partner-logo" />
            <div>
              <p className="text-sm text-slate-600">{user?.name} - {translatePartnerType(partnerType, t)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="partner-lang-switcher" className="w-28 h-8 text-xs border-slate-200">
                <Globe size={14} className="mr-1" />
                <SelectValue>{LANG_LABELS[language]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="vi">Tiếng Việt</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="partner-logout-button" onClick={handleLogout} variant="ghost" className="text-slate-600">
              <LogOut size={18} className="mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>

        {/* Notification banner */}
        {notifBanner && (
          <div data-testid="new-order-notification" className="absolute top-full left-0 right-0 z-50 animate-in slide-in-from-top">
            <div className="max-w-7xl mx-auto px-6 py-3">
              <div className="bg-red-600 text-white rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
                <Bell size={20} className="animate-bounce" />
                <span className="font-semibold">{notifBanner}</span>
                <button onClick={() => setNotifBanner(null)} className="ml-auto text-red-200 hover:text-white text-lg font-bold">&times;</button>
              </div>
            </div>
          </div>
        )}
        {/* Orange/Red alert notifications */}
        {alertNotifs.length > 0 && (
          <div data-testid="color-alert-notification" className="absolute top-full left-0 right-0 z-50" style={{marginTop: notifBanner ? '60px' : '0'}}>
            <div className="max-w-7xl mx-auto px-6 py-2 space-y-2">
              {alertNotifs.map(n => (
                <div key={n.id} className={`rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg ${n.msg.includes(t('alert_red_notification')) ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                  <Bell size={20} className="animate-bounce" />
                  <span className="font-semibold">{n.msg}</span>
                  <button onClick={() => setAlertNotifs(prev => prev.filter(x => x.id !== n.id))} className="ml-auto opacity-70 hover:opacity-100 text-lg font-bold">&times;</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Production stats banners for imprimeur/couture */}
        {(isCouture || isImprimeur) && prodStats && (
          <div className="space-y-3">
            <div className="flex gap-3">
              {/* Total pieces this year */}
              <Card className="flex-1 border-blue-200 bg-blue-50">
                <CardContent className="py-4 flex items-center gap-4">
                  <Factory size={28} className="text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-700">{t('prod_this_year')}</p>
                    <p className="text-3xl font-black text-blue-900">{(prodStats.total_pieces_year || 0).toLocaleString()} <span className="text-base font-semibold">{t('prod_pieces')}</span></p>
                  </div>
                </CardContent>
              </Card>
              {/* Late orders this month */}
              {prodStats.late_this_month_count > 0 && (
                <Card className="flex-1 border-red-300 bg-red-50">
                  <CardContent className="py-4 flex items-center gap-4">
                    <AlertTriangle size={28} className="text-red-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-800">{t('prod_late_title')} <span className="text-2xl font-black">{prodStats.late_this_month_count}</span> {t('prod_late_suffix')}</p>
                      <Button size="sm" className="mt-2 bg-red-600 hover:bg-red-700 text-white text-xs gap-1" onClick={() => setLateDialogOpen(true)}>
                        <AlertTriangle size={12} /> {t('prod_late_see')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            {/* Important warning message */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">{t('prod_warning')}</p>
            </div>
          </div>
        )}

        {/* Late orders dialog */}
        <Dialog open={lateDialogOpen} onOpenChange={setLateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle size={20} /> {t('prod_late_see')}</DialogTitle>
            </DialogHeader>
            {prodStats?.late_this_month?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('prod_late_order')}</TableHead>
                    <TableHead>{t('prod_late_deadline')}</TableHead>
                    <TableHead>{t('prod_late_changed')}</TableHead>
                    <TableHead>{t('prod_late_days')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prodStats.late_this_month.map(lo => (
                    <TableRow key={lo.id} className="bg-red-50">
                      <TableCell>
                        <p className="font-bold text-sm">{lo.order_number}</p>
                        <p className="text-xs text-slate-500">{lo.client_name}</p>
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-red-700">{lo.couture_deadline ? new Date(lo.couture_deadline).toLocaleDateString('fr-FR') : '—'}</TableCell>
                      <TableCell className="text-sm">{lo.status_changed_at ? new Date(lo.status_changed_at).toLocaleDateString('fr-FR') : <span className="text-red-600 font-semibold">{t('prod_late_still')}</span>}</TableCell>
                      <TableCell><Badge className="bg-red-100 text-red-800">{lo.days_late} {t('prod_late_days')}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Aucune commande en retard ce mois-ci</p>
            )}
          </DialogContent>
        </Dialog>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('partner_orders')}</h2>
            <p className="text-sm text-slate-600 mt-1">
              {translateStatus(allowedStatuses[0] || '', t)} - {t('partner_orders_subtitle')}
            </p>
            <StatusFlowBar />
          </div>
          <div className="flex items-center gap-2">
            {notifPermission !== 'granted' ? (
              <Button data-testid="enable-notifications-btn" variant="outline" size="sm" onClick={requestNotifPermission} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <Bell size={14} className="mr-1" /> {t('enable_notifications')}
              </Button>
            ) : (
              <Badge data-testid="notifications-enabled-badge" className="bg-emerald-100 text-emerald-700 border border-emerald-200 h-8 px-3">
                <Bell size={14} className="mr-1" /> {t('notifications_enabled')}
              </Badge>
            )}
            <Button data-testid="my-info-button" variant="outline" size="sm" onClick={() => setMyInfoOpen(true)}>
              {t('my_information')}
            </Button>
          </div>
        </div>

        {/* Checkin Create Colis Dialog */}
        <Dialog open={checkinColisDialogOpen} onOpenChange={setCheckinColisDialogOpen}>
          <DialogContent data-testid="checkin-colis-dialog">
            <DialogHeader>
              <DialogTitle>{t('create_colis')} ({Object.keys(checkinSelectedOrders).filter(k => checkinSelectedOrders[k]).length} {t('colis_orders_count')})</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{t('colis_name') || 'Nom du colis'}</Label>
                <Input data-testid="checkin-colis-name-input" value={checkinColisName} onChange={e => setCheckinColisName(e.target.value)} placeholder="Ex: Lot France Août 2026" className="mt-1" />
              </div>
              <div className="text-xs text-slate-500">
                {Object.keys(checkinSelectedOrders).filter(k => checkinSelectedOrders[k]).map(id => {
                  const o = orders.find(x => x.id === id);
                  return o ? o.order_number : id;
                }).join(', ')}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCheckinColisDialogOpen(false)}>{t('cancel')}</Button>
                <Button data-testid="checkin-colis-confirm-btn" className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateCheckinColis} disabled={!checkinColisName.trim()}>
                  <Package size={14} className="mr-1" /> {t('create_colis')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* MES INFORMATIONS Popup */}
        <Dialog open={myInfoOpen} onOpenChange={setMyInfoOpen}>
          <DialogContent className="max-w-md" data-testid="my-info-dialog">
            <DialogHeader>
              <DialogTitle>{t('my_information')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p data-testid="my-info-email" className="text-sm font-medium">{myInfo?.email || user?.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">{t('password')}</Label>
                  <p className="text-sm font-medium">********</p>
                </div>
                <div className="col-span-full">
                  <Label className="text-xs text-slate-500">{t('allowed_statuses')}</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(myInfo?.allowed_statuses || allowedStatuses || []).map((s, i) => (
                      <Badge key={`status-${s}`} variant="outline" className="text-xs">{translateStatus(s, t)}</Badge>
                    ))}
                  </div>
                </div>
              </div>

              {!showChangePassword ? (
                <Button data-testid="change-password-btn" variant="outline" size="sm" onClick={() => setShowChangePassword(true)} className="w-full">
                  {t('change_password')}
                </Button>
              ) : (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('current_password')}</Label>
                      <Input data-testid="current-password-input" type="password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('new_password')}</Label>
                      <Input data-testid="new-password-input" type="password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('confirm_password')}</Label>
                      <Input data-testid="confirm-password-input" type="password" value={pwdForm.confirm_password} onChange={(e) => setPwdForm({ ...pwdForm, confirm_password: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="submit-password-change" size="sm" onClick={handleChangePassword} disabled={pwdLoading} className="flex-1 bg-slate-900 hover:bg-slate-800">
                      {pwdLoading ? '...' : t('save')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowChangePassword(false); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }}>{t('cancel')}</Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Transport sidebar navigation */}
        {isTransport && (
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              data-testid="transport-tab-dashboard"
              variant={activeTransportTab === 'orders' ? 'default' : 'outline'}
              className={activeTransportTab === 'orders' ? 'bg-slate-900' : ''}
              onClick={() => setActiveTransportTab('orders')}
            >
              <LayoutDashboard size={14} className="mr-1" /> {t('dashboard')}
            </Button>
            <Button
              size="sm"
              data-testid="transport-tab-archives"
              variant={activeTransportTab === 'archives' ? 'default' : 'outline'}
              className={activeTransportTab === 'archives' ? 'bg-slate-900' : ''}
              onClick={() => setActiveTransportTab('archives')}
            >
              <ArchiveIcon size={14} className="mr-1" /> {t('archives_title')}
            </Button>
            <Button
              size="sm"
              data-testid="transport-tab-invoices"
              variant={activeTransportTab === 'invoices' ? 'default' : 'outline'}
              className={activeTransportTab === 'invoices' ? 'bg-slate-900' : ''}
              onClick={() => setActiveTransportTab('invoices')}
            >
              <FileText size={14} className="mr-1" /> FACTURES
            </Button>
          </div>
        )}

        {(!isTransport || activeTransportTab === 'orders') && (
        <Card data-testid="partner-orders-card" className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium">
                {orders.length} {t('pending_orders')}
              </CardTitle>
              {isTransport && selectedColisIds.length >= 2 && (
                <Button data-testid="create-colis-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={openColisDialog}>
                  <Package size={16} /> {t('create_colis')} ({selectedColisIds.length})
                </Button>
              )}
              {isCheckin && Object.keys(checkinSelectedOrders).filter(k => checkinSelectedOrders[k]).length >= 1 && (
                <Button data-testid="checkin-create-colis-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                  onClick={() => setCheckinColisDialogOpen(true)}>
                  <Package size={16} /> {t('create_colis')} ({Object.keys(checkinSelectedOrders).filter(k => checkinSelectedOrders[k]).length})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">{t('no_pending_orders')}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium w-14"></TableHead>
                      {isCheckin && <TableHead className="w-10"></TableHead>}
                      {isTransport && <TableHead className="w-10"></TableHead>}
                      <TableHead className="font-medium">{t('order_number')}</TableHead>
                      {!isTransport && <TableHead className="font-medium">{t('company')}</TableHead>}
                      {!isTransport && <TableHead className="font-medium">{t('country')}</TableHead>}
                      {isTransport && <TableHead className="font-medium">{t('address')}</TableHead>}
                      {(isCouture || isImprimeur) && <TableHead className="font-medium">{t('tissu')}</TableHead>}
                      {(isCouture || isImprimeur) && <TableHead className="font-medium">{t('quantities')}</TableHead>}
                      {isCouture && <TableHead className="font-medium">{t('order_date')}</TableHead>}
                      {isCouture && <TableHead className="font-medium">{t('delivery_office')}</TableHead>}
                      {isImprimeur && <TableHead className="font-medium">{t('delivery_couture')}</TableHead>}
                      {showLivraisonPrevue && <TableHead className="font-medium">{t('delivery_expected')}</TableHead>}
                      {!isTransport && <TableHead className="font-medium">{t('urgency')}</TableHead>}
                      {showWeight && !isTransport && <TableHead className="font-medium">{t('weight_label')}</TableHead>}
                      <TableHead className="font-medium">{t('status')}</TableHead>
                      {isTransport && <TableHead className="font-medium">{t('add_tracking')}</TableHead>}
                      {isTransport && <TableHead className="font-medium">{t('weight_kg')} / {t('price_vnd')}</TableHead>}
                      {isTransport && <TableHead className="font-medium">{t('delivery_col')}</TableHead>}
                      {(isImprimeur || isCreateurFichiers) && <TableHead className="font-medium">DROPBOX</TableHead>}
                      {hasStatusAction && <TableHead className="font-medium">{t('actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...orders].sort((a, b) => {
                      if (isTransport) {
                        const aHas = a.tracking_number ? 1 : 0;
                        const bHas = b.tracking_number ? 1 : 0;
                        return aHas - bHas;
                      }
                      return 0;
                    }).map((order) => (
                      <TableRow
                        key={order.id}
                        data-testid="partner-order-row"
                        className={`hover:bg-slate-100 transition-colors ${getRowColor(order, partnerType)}`}
                      >
                        <TableCell>
                          {order.images?.length > 0 ? (
                            <img
                              src={getImageUrl(order.images[0])}
                              alt="" data-testid="order-thumbnail"
                              className="w-10 h-10 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80"
                              onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded border border-slate-300 flex items-center justify-center">
                              <ImageIcon size={14} className="text-slate-400" />
                            </div>
                          )}
                        </TableCell>
                        {isCheckin && !order.transport_package_id && (
                          <TableCell>
                            <Checkbox data-testid={`checkin-select-${order.id}`} checked={!!checkinSelectedOrders[order.id]} onCheckedChange={() => setCheckinSelectedOrders(prev => ({ ...prev, [order.id]: !prev[order.id] }))} />
                          </TableCell>
                        )}
                        {isCheckin && order.transport_package_id && (
                          <TableCell>
                            <Package size={14} className="text-blue-500" />
                          </TableCell>
                        )}
                        {isTransport && (
                          <TableCell>
                            <Checkbox data-testid={`colis-checkbox-${order.id}`} checked={!!selectedForColis[order.id]} onCheckedChange={() => setSelectedForColis(prev => ({ ...prev, [order.id]: !prev[order.id] }))} />
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          {order.order_number}
                          {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                        </TableCell>
                        {!isTransport && <TableCell>{order.client_company}</TableCell>}
                        {!isTransport && <TableCell>{order.client_country}</TableCell>}
                        {isTransport && (
                          <TableCell>
                            <Button data-testid="partner-address-button" size="sm" variant="outline" className="text-xs uppercase"
                              onClick={() => { setAddressOrder(order); setAddressDialogOpen(true); }}>
                              <MapPin size={14} className="mr-1" />
                              {t('address')}
                            </Button>
                          </TableCell>
                        )}
                        {(isCouture || isImprimeur) && (
                          <TableCell><span className="font-bold text-emerald-600">{order.tissu || '—'}</span></TableCell>
                        )}
                        {(isCouture || isImprimeur) && (
                          <TableCell>
                            <Button data-testid="quantities-btn" size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => { setQuantityOrder(order); setQuantityDialogOpen(true); }}>
                              {t('quantities')}
                            </Button>
                          </TableCell>
                        )}
                        {isCouture && (
                          <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                        )}
                        {isCouture && (
                          <TableCell className="text-sm font-medium">
                            {order.bureau_delivery_date ? new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                        )}
                        {isImprimeur && (
                          <TableCell className="text-sm font-medium">
                            {(() => {
                              const od = new Date(order.order_date);
                              const urg = (order.urgency || '').toLowerCase();
                              const days = urg === 'urgent' ? 1 : urg === 'non urgent' ? 5 : 3;
                              od.setDate(od.getDate() + days);
                              return od.toLocaleDateString('fr-FR');
                            })()}
                          </TableCell>
                        )}
                        {showLivraisonPrevue && (
                          <TableCell className="text-sm">{new Date(order.delivery_date).toLocaleDateString('fr-FR')}</TableCell>
                        )}
                        {!isTransport && (
                        <TableCell>
                          <Badge className={`border text-xs uppercase ${
                            order.urgency === 'urgent' ? 'bg-red-100 text-red-800 border-red-200' :
                            order.urgency === 'prioritaire' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                            order.urgency === 'classique' ? 'bg-slate-100 text-slate-800 border-slate-200' :
                            'bg-green-100 text-green-800 border-green-200'
                          }`}>
                            {translateUrgency(order.urgency, t)}
                          </Badge>
                        </TableCell>
                        )}
                        {showWeight && !isTransport && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input data-testid="partner-weight-input" type="number" step="0.1" min="0"
                                value={weightInputs[order.id] || ''}
                                onChange={(e) => setWeightInputs({ ...weightInputs, [order.id]: e.target.value })}
                                className="w-20 h-8 text-sm" />
                              <Button data-testid="partner-save-weight-button" size="sm" variant="outline" className="h-8 px-2"
                                onClick={() => handleWeightUpdate(order.id)}>
                                <Package size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs uppercase">
                              {translateStatus(order.status, t)}
                            </Badge>
                            {order.alert && (
                              <span data-testid="order-alert-blink" className="alert-blink bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded uppercase">ALERT</span>
                            )}
                          </div>
                        </TableCell>
                        {isTransport && (
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              {!order.tracking_number?.trim() && !order.tracking_link?.trim() && (
                                <Button data-testid="add-tracking-btn" size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => openTrackingDialog(order)}>
                                  <Truck size={12} /> {t('add_tracking')}
                                </Button>
                              )}
                              {order.tracking_number && (
                                <span className="text-xs font-medium text-slate-700">{t('package')}: {order.tracking_number}</span>
                              )}
                              {order.tracking_number && (
                                <Button data-testid="tracking-detail-btn" size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => {
                                  if (isTransport) {
                                    window.open(`${window.location.origin}/track?n=${order.tracking_number}`, '_blank');
                                  } else {
                                    setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true);
                                  }
                                }}>
                                  <Package size={12} /> SUIVI
                                </Button>
                              )}
                              {!isTransport && <DeliveredBadge date={order.tracking_delivered_date} />}
                              {(order.tracking_number || order.tracking_link) && (
                                <Button size="sm" variant="ghost" className="h-5 text-xs text-slate-500 px-1" onClick={() => openTrackingDialog(order)}>{t('edit')}</Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                        {isTransport && (
                          <TableCell>
                            {order.transport_validated ? (
                              <div className="text-xs space-y-0.5">
                                <div className="font-bold text-emerald-700">{order.package_weight} kg</div>
                                <div className="font-bold">{(order.package_price_vnd || 0).toLocaleString()} VND</div>
                              </div>
                            ) : (
                              <Button data-testid="add-weight-btn" size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => openPackageDialog(order.id)}>
                                <Package size={12} /> {t('add_weight')}
                              </Button>
                            )}
                          </TableCell>
                        )}
                        {isTransport && (
                          <TableCell>
                            {order.tracking_delivered_date ? (
                              <span className="text-xs font-bold text-emerald-700">
                                {t('delivered_short')} {new Date(order.tracking_delivered_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            ) : order.tracking_first_event_date ? (
                              (() => {
                                const est = new Date(new Date(order.tracking_first_event_date).getTime() + 15*24*60*60*1000);
                                const estStr = est.toISOString().slice(0, 10);
                                const todayStr = new Date().toISOString().slice(0, 10);
                                const overdue = estStr < todayStr;
                                return (
                                  <span className={`text-xs font-bold ${overdue ? 'text-red-600 animate-pulse' : 'text-emerald-600'}`}>
                                    {t('estimated_short')} {est.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                );
                              })()
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                        )}
                        {(isImprimeur || isCreateurFichiers) && (
                          <TableCell>
                            {order.dropbox_link ? (
                              <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                <Button data-testid="partner-dropbox-button" size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                              </a>
                            ) : <span className="text-xs text-slate-400">-</span>}
                          </TableCell>
                        )}
                        {hasStatusAction && (() => {
                          const ns = getNextStatus(order.status);
                          return ns ? (
                            <TableCell>
                              <Button data-testid="partner-change-status-button" size="sm" className="bg-slate-900 hover:bg-slate-800 text-xs uppercase"
                                onClick={() => handleStatusChange(order.id, ns)}>
                                <ArrowRight size={14} className="mr-1" />
                                {translateStatus(ns, t)}
                              </Button>
                            </TableCell>
                          ) : <TableCell />;
                        })()}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {isTransport && (
          <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
            <DialogContent data-testid="address-dialog">
              <DialogHeader>
                <DialogTitle>{t('address_delivery')} - {addressOrder?.order_number}</DialogTitle>
              </DialogHeader>
              {addressOrder && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">{t('name')}</Label>
                      <p className="font-medium text-lg">{addressOrder.client_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('firstname')}</Label>
                      <p className="font-medium text-lg">{addressOrder.client_firstname || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('address')}</Label>
                    <p className="font-medium">{addressOrder.client_address || t('not_provided')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">{t('postal_code')}</Label>
                      <p className="font-medium" data-testid="address-postal-code">{addressOrder.client_postal_code || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('city')}</Label>
                      <p className="font-medium" data-testid="address-city">{addressOrder.client_city || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">{t('country')}</Label>
                      <p className="font-medium">{addressOrder.client_country}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('phone')}</Label>
                      <p className="font-medium">{addressOrder.client_phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* TRANSPORT FACTURES SECTION */}
        {isTransport && activeTransportTab === 'invoices' && (
          <Card data-testid="transport-invoices-card" className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArchiveIcon size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">FACTURES</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {/* Uninvoiced packages */}
              {transportPackages.filter(p => !p.invoiced).length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700">Colis non facturés</h3>
                    {Object.keys(selectedPackageIds).filter(k => selectedPackageIds[k]).length > 0 && (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1" onClick={handleCreateInvoice}>
                        Créer Facture ({Object.keys(selectedPackageIds).filter(k => selectedPackageIds[k]).length})
                      </Button>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead className="font-medium">Commandes</TableHead>
                        <TableHead className="font-medium">{t('weight_kg')}</TableHead>
                        <TableHead className="font-medium">{t('price_vnd')}</TableHead>
                        <TableHead className="font-medium">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transportPackages.filter(p => !p.invoiced).map(pkg => (
                        <TableRow key={pkg._id}>
                          <TableCell>
                            <Checkbox checked={!!selectedPackageIds[pkg._id]} onCheckedChange={() => setSelectedPackageIds(prev => ({ ...prev, [pkg._id]: !prev[pkg._id] }))} />
                          </TableCell>
                          <TableCell className="text-xs font-medium">{pkg.order_numbers?.join(', ')}</TableCell>
                          <TableCell className="text-xs">{pkg.weight_kg} kg</TableCell>
                          <TableCell className="text-xs">{pkg.price_vnd?.toLocaleString()}đ</TableCell>
                          <TableCell className="text-xs">{pkg.created_at?.slice(0, 10)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Invoices list */}
              {transportInvoices.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Factures</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-medium">N°</TableHead>
                        <TableHead className="font-medium">Date</TableHead>
                        <TableHead className="font-medium">Colis</TableHead>
                        <TableHead className="font-medium">Poids</TableHead>
                        <TableHead className="font-medium">Total VND</TableHead>
                        <TableHead className="font-medium">Statut</TableHead>
                        <TableHead className="font-medium">PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transportInvoices.map(inv => (
                        <TableRow key={inv._id}>
                          <TableCell className="font-bold text-sm">{inv.invoice_number}</TableCell>
                          <TableCell className="text-xs">{inv.date?.slice(0, 10)}</TableCell>
                          <TableCell className="text-xs">{inv.lines?.length || 0}</TableCell>
                          <TableCell className="text-xs">{inv.total_weight_kg?.toFixed(1)} kg</TableCell>
                          <TableCell className="text-xs font-semibold">{inv.total_vnd?.toLocaleString()}đ</TableCell>
                          <TableCell>
                            <Badge className={inv.paid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                              {inv.paid ? 'Payée' : 'En attente'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => handleDownloadInvoicePdf(inv._id, inv.invoice_number)}>
                              PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {transportPackages.filter(p => !p.invoiced).length === 0 && transportInvoices.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Aucune facture</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ARCHIVES SECTION for all partners */}
        {(!isTransport || activeTransportTab === 'archives') && archives.length > 0 && (
          <Card data-testid="partner-archives-card" className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ArchiveIcon size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">{t('partner_archives')}</CardTitle>
              </div>
              <p className="text-xs text-slate-500 mt-1">{archives.length} {t('partner_archives_subtitle')}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {groupByMonth(archives).map((group) => (
                  <div key={group.label} data-testid="partner-archive-month-group" className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      data-testid="toggle-partner-archive-month"
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      onClick={() => toggleMonth(group.label)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                        <span className="font-semibold text-slate-900">{group.label}</span>
                      </div>
                      <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                    </button>
                    {expandedMonths[group.label] && (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white">
                              <TableHead className="font-medium w-14"></TableHead>
                              <TableHead className="font-medium">{t('order_number')}</TableHead>
                              {!isTransport && <TableHead className="font-medium">{t('company')}</TableHead>}
                              {!isTransport && <TableHead className="font-medium">{t('country')}</TableHead>}
                              <TableHead className="font-medium">{t('order_date')}</TableHead>
                              {!isTransport && !isImprimeur && <TableHead className="font-medium">{t('delivery_office')}</TableHead>}
                              <TableHead className="font-medium">{t('status')}</TableHead>
                              {isTransport && <TableHead className="font-medium">{t('weight_kg')} / {t('price_vnd')}</TableHead>}
                              {isTransport && <TableHead className="font-medium">{t('delivery_col')}</TableHead>}
                              {!isTransport && !isCouture && !isImprimeur && <TableHead className="font-medium">DROPBOX</TableHead>}
                              <TableHead className="font-medium">Suivi</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.orders.map((order) => (
                              <TableRow key={order.id} data-testid="partner-archive-row" className="hover:bg-slate-50">
                                <TableCell>
                                  {order.images?.length > 0 ? (
                                    <img src={getImageUrl(order.images[0])} alt=""
                                      className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }} />
                                  ) : (
                                    <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                      <ImageIcon size={14} className="text-slate-400" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">
                                  {order.order_number}
                                  {hasExpress(order) && <span data-testid="express-badge" className="ml-2 inline-block bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">EXPRESS</span>}
                                </TableCell>
                                {!isTransport && <TableCell>{order.client_company}</TableCell>}
                                {!isTransport && <TableCell>{order.client_country}</TableCell>}
                                <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                                {!isTransport && !isImprimeur && <TableCell className="text-sm font-medium">{order.bureau_delivery_date ? new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}</TableCell>}
                                <TableCell>
                                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{translateStatus(order.status, t)}</Badge>
                                </TableCell>
                                {isTransport && (
                                  <TableCell>
                                    <div className="text-xs">
                                      {order.package_weight ? <div className="font-bold text-emerald-700">{order.package_weight} kg</div> : null}
                                      {order.package_price_vnd ? <div className="font-bold">{order.package_price_vnd.toLocaleString()} VND</div> : null}
                                      {!order.package_weight && !order.package_price_vnd && <span className="text-slate-400">—</span>}
                                    </div>
                                  </TableCell>
                                )}
                                {isTransport && (
                                  <TableCell>
                                    {order.tracking_delivered_date ? (
                                      <span className="text-xs font-bold text-emerald-700">
                                        {t('delivered_short')} {new Date(order.tracking_delivered_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </span>
                                    ) : order.tracking_first_event_date ? (
                                      (() => {
                                        const est = new Date(new Date(order.tracking_first_event_date).getTime() + 15*24*60*60*1000);
                                        const estStr = est.toISOString().slice(0, 10);
                                        const todayStr = new Date().toISOString().slice(0, 10);
                                        const overdue = estStr < todayStr;
                                        return (
                                          <span className={`text-xs font-bold ${overdue ? 'text-red-600 animate-pulse' : 'text-emerald-600'}`}>
                                            {t('estimated_short')} {est.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                          </span>
                                        );
                                      })()
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                  </TableCell>
                                )}
                                {!isTransport && !isCouture && !isImprimeur && (
                                <TableCell>
                                  {order.dropbox_link ? (
                                    <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                                    </a>
                                  ) : <span className="text-xs text-slate-400">-</span>}
                                </TableCell>
                                )}
                                <TableCell>
                                  {order.tracking_number && (
                                    <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => {
                                      e.stopPropagation();
                                      if (isTransport) {
                                        window.open(`${window.location.origin}/track?n=${order.tracking_number}`, '_blank');
                                      } else {
                                        setTrackingDetailNumber(order.tracking_number); setTrackingDetailOpen(true);
                                      }
                                    }}>
                                      <Package size={12} /> SUIVI
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
          <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
            <DialogHeader>
              <DialogTitle>{t('image_popup_title')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {imagePopupImages.map((img, i) => (
                <img key={`popup-${img}`} src={getImageUrl(img)} alt="" className="w-full rounded-lg border border-slate-200" />
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
          <DialogContent className="max-w-md" data-testid="tracking-dialog">
            <DialogHeader>
              <DialogTitle>Tracking — {trackingOrder?.order_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">{t('tracking_link_label')}</Label>
                <Input data-testid="tracking-link-input" value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="https://..." className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">{t('tracking_number_label')}</Label>
                <Input data-testid="tracking-number-input" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Ex: FR123456789" className="mt-1" />
              </div>
              <Button data-testid="save-tracking-btn" className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleSaveTracking}>{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={quantityDialogOpen} onOpenChange={setQuantityDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="quantity-dialog">
            <DialogHeader>
              <DialogTitle>{t('quantities_detail')} — {quantityOrder?.order_number}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {quantityOrder?.tissu && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('tissu')} :</span>
                  <span className="font-bold text-emerald-600">{quantityOrder.tissu}</span>
                </div>
              )}
              {quantityOrder?.order_lines && quantityOrder.order_lines.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('product_name')}</TableHead>
                      <TableHead>{t('tissu')}</TableHead>
                      <TableHead className="text-right">{t('quantity')}</TableHead>
                      <TableHead className="text-right">{t('weight_label')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quantityOrder.order_lines.map((line, i) => (
                      <TableRow key={`${line.product_name}-${i}`}>
                        <TableCell className="font-medium">{line.product_name}</TableCell>
                        <TableCell className="font-bold text-emerald-600">{line.tissu || '—'}</TableCell>
                        <TableCell className="text-right">{line.quantity}</TableCell>
                        <TableCell className="text-right">{line.weight_kg} kg</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-sm text-slate-500">
                  <p>{t('piece_type')} : {quantityOrder?.piece_type || '—'}</p>
                  <p>{t('quantity')} : {quantityOrder?.num_pieces || 0}</p>
                  <p>{t('estimated_weight')} : {quantityOrder?.package_weight_kg || 0} kg</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Package weight/price dialog */}
        <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
          <DialogContent data-testid="package-dialog">
            <DialogHeader>
              <DialogTitle>{t('validate_package')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">{t('weight_kg')}</Label>
                <Input data-testid="package-weight-input" type="number" step="0.1" min="0" value={packageWeight} onChange={e => setPackageWeight(e.target.value)} placeholder="Ex: 12.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">{t('price_vnd')}</Label>
                <Input data-testid="package-price-input" type="number" step="1000" min="0" value={packagePrice} onChange={e => setPackagePrice(e.target.value)} placeholder="Ex: 500000" />
              </div>
              <p className="text-xs text-red-500 font-medium">{t('transport_validate_warning')}</p>
              <div className="flex justify-end gap-2">
                <Button data-testid="package-cancel-btn" variant="outline" onClick={() => setPackageDialogOpen(false)}>{t('cancel')}</Button>
                <Button data-testid="package-validate-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleValidatePackage}>
                  <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Group colis dialog */}
        <Dialog open={colisDialogOpen} onOpenChange={setColisDialogOpen}>
          <DialogContent data-testid="colis-dialog">
            <DialogHeader>
              <DialogTitle>{t('create_colis')} ({selectedColisIds.length} commandes)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold">{t('weight_kg')} total</Label>
                  <Input type="number" step="0.1" min="0" value={colisWeight} onChange={e => setColisWeight(e.target.value)} placeholder="Poids total" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{t('price_vnd')} total</Label>
                  <Input type="number" step="1000" min="0" value={colisPrice} onChange={e => setColisPrice(e.target.value)} placeholder="Prix total" />
                </div>
              </div>
              <p className="text-xs text-red-500 font-medium">{t('transport_validate_warning')}</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setColisDialogOpen(false)}>{t('cancel')}</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleValidateColis}>
                  <CheckCircle size={14} className="mr-1" /> {t('validate_definitive')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <TrackingDialog
          open={trackingDetailOpen}
          onOpenChange={setTrackingDetailOpen}
          trackingNumber={trackingDetailNumber}
        />
      </main>
    </div>
  );
};
